import { type FC } from "react";
import {
  Checkbox,
  Typography,
  Card,
  Grid,
  Box,
  Stack,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { countriesByCode } from "@/shared/utils/countries";
import { type CountryAccessesItem } from "@/partner/entities/countryAccesses";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import type { CountryAccessTypeType } from "@/types/graphql/partner.generated";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  padding: theme.spacing(2, 1),
  overflow: "visible",
  position: "relative",
}));

// Сколько стран показывать в сводке правил перед обрезкой с «…»
const MAX_COUNTRIES_IN_SUMMARY = 10;

const TYPE_LABEL_KEYS = {
  ALL: "countryAccesses.type.ALL",
  INCLUDE: "countryAccesses.type.INCLUDE",
  EXCLUDE: "countryAccesses.type.EXCLUDE",
} as Record<CountryAccessTypeType, string>;

interface CountryAccessesListItemProps {
  item: CountryAccessesItem;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

export const CountryAccessesListItem: FC<CountryAccessesListItemProps> = ({
  item,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();

  const rulesSummary =
    item.type === "ALL"
      ? t("countryAccesses.list.allCountries")
      : (() => {
          const names = item.countries.map(
            (code) => countriesByCode[code] ?? code
          );
          const truncated = names.length > MAX_COUNTRIES_IN_SUMMARY;
          const displayed = names.slice(0, MAX_COUNTRIES_IN_SUMMARY).join(", ");

          return `${t(TYPE_LABEL_KEYS[item.type])}: ${displayed}${
            truncated ? "…" : ""
          }`;
        })();

  return (
    <StyledCard selected={selected} elevation={0}>
      <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
        <Grid size="auto">
          <Checkbox
            color="default"
            checked={selected}
            onChange={(event) => onSelectOneChange(item.id, event)}
            onClick={(event) => event.stopPropagation()}
          />
        </Grid>

        <Grid size="grow">
          <Box
            sx={{
              ml: { xs: 1.5, md: 0 },
              mr: { xs: 1.5, md: 0 },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ItemTitleLink
                    to={`${PagePaths.countryAccesses}?tab=edit&id=${item.id}`}
                    onOpen={() => onClickItem(item.id)}
                    underline="hover"
                    variant="h6"
                    color="textPrimary"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
                    }}
                  >
                    {item.title}
                  </ItemTitleLink>
                  {item.isDefault && (
                    <Chip
                      size="small"
                      label={t("countryAccesses.list.defaultBadge")}
                    />
                  )}
                </Stack>

                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {rulesSummary}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Grid>

        <Grid size="auto">
          <Typography variant="body2" color="textPrimary" align="right">
            {t("countryAccesses.list.contentsCount", {
              count: item.contentsCount,
            })}
          </Typography>
        </Grid>

        <Grid size="auto" container justifyContent="flex-end">
          <Box>
            <Typography variant="body2" color="textPrimary" align="right">
              {t("countryAccesses.item.createdAtTime", {
                createdAt: item.createdAt,
              })}
            </Typography>
            <Typography variant="body2" color="textSecondary" align="right">
              {t("countryAccesses.item.createdAtDate", {
                createdAt: item.createdAt,
              })}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
